import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import {
  ChefHat, Loader2, ArrowLeft, ArrowRight, Sparkles, Check,
  MapPin, DollarSign, Building2, Wand2, Zap, BarChart3,
  ShoppingBag, Bot, Brain, Search, Clock, TrendingUp, Users
} from 'lucide-react';
import axios from 'axios';
import { CATEGORY_GROUPS } from '../config/businessTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRICE_LEVELS = [
  { id: 'budget', ar: 'اقتصادي', en: 'Budget', color: '#10B981' },
  { id: 'mid', ar: 'متوسط', en: 'Mid-range', color: '#F59E0B' },
  { id: 'premium', ar: 'فاخر', en: 'Premium', color: '#8B5CF6' },
];

const CITIES = [
  { id: 'riyadh', ar: 'الرياض', en: 'Riyadh' },
  { id: 'jeddah', ar: 'جدة', en: 'Jeddah' },
  { id: 'dammam', ar: 'الدمام', en: 'Dammam' },
  { id: 'makkah', ar: 'مكة', en: 'Makkah' },
  { id: 'madinah', ar: 'المدينة', en: 'Madinah' },
  { id: 'other', ar: 'أخرى', en: 'Other' },
];

const LOADING_STEPS = {
  ar: [
    { text: 'جاري تحميل المنيو...', icon: Wand2 },
    { text: 'توليد طلبات تجريبية...', icon: ShoppingBag },
    { text: 'إعداد التحليلات المالية...', icon: BarChart3 },
    { text: 'تهيئة وكيل الطلبات الذكي...', icon: Bot },
    { text: 'تجهيز لوحة التحكم...', icon: Zap },
  ],
  en: [
    { text: 'Loading your menu...', icon: Wand2 },
    { text: 'Generating demo orders...', icon: ShoppingBag },
    { text: 'Setting up analytics...', icon: BarChart3 },
    { text: 'Configuring order agent...', icon: Bot },
    { text: 'Preparing dashboard...', icon: Zap },
  ],
};

export default function OnboardPage() {
  const { demoLogin } = useAuth();
  const { lang, isAr, dir, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [businessTypes, setBusinessTypes] = useState([]);
  const [businessType, setBusinessType] = useState(searchParams.get('type') || null);
  const [businessName, setBusinessName] = useState('');
  const [priceLevel, setPriceLevel] = useState('mid');
  const [city, setCity] = useState(searchParams.get('city') || 'riyadh');
  const [activeGroup, setActiveGroup] = useState(searchParams.get('type') ? null : 'fast_food');
  const [searchQuery, setSearchQuery] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingDone, setLoadingDone] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(10);
  const loadingRef = useRef(false);

  const l = (ar, en) => isAr ? ar : en;

  // Load business types
  useEffect(() => {
    axios.get(`${API}/business-types`).then(res => {
      setBusinessTypes(res.data || []);
      // Auto-detect group if type came from URL
      if (searchParams.get('type')) {
        const t = (res.data || []).find(x => x.id === searchParams.get('type'));
        if (t) setActiveGroup(t.category_group);
      }
    }).catch(() => {});
  }, []);

  // Load preview when type or price changes
  useEffect(() => {
    if (!businessType) { setPreview(null); return; }
    setPreviewLoading(true);
    axios.get(`${API}/business-types/${businessType}/preview?price_level=${priceLevel}`)
      .then(res => setPreview(res.data))
      .catch(() => {})
      .finally(() => setPreviewLoading(false));
  }, [businessType, priceLevel]);

  const selectedType = businessTypes.find(t => t.id === businessType);
  const accentColor = selectedType?.color || '#D97706';

  // Group & filter
  const groupedTypes = useMemo(() => {
    const g = {};
    businessTypes.forEach(t => {
      const k = t.category_group || 'other';
      if (!g[k]) g[k] = [];
      g[k].push(t);
    });
    return g;
  }, [businessTypes]);

  const sortedGroups = Object.keys(groupedTypes).sort((a, b) =>
    (CATEGORY_GROUPS[a]?.order || 99) - (CATEGORY_GROUPS[b]?.order || 99)
  );

  const filteredTypes = searchQuery.trim()
    ? businessTypes.filter(t => t.name_ar?.includes(searchQuery) || t.name_en?.toLowerCase().includes(searchQuery.toLowerCase()))
    : groupedTypes[activeGroup] || [];

  // Auto-set group when type selected
  const handleSelectType = (type) => {
    setBusinessType(type.id);
    setError('');
    if (!activeGroup || !groupedTypes[activeGroup]?.find(t => t.id === type.id)) {
      setActiveGroup(type.category_group);
    }
  };

  // Loading animation + countdown
  useEffect(() => {
    if (!loading) return;
    const stepInterval = setInterval(() => {
      setLoadingStep(prev => prev < 4 ? prev + 1 : prev);
    }, 1800);
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => { clearInterval(stepInterval); clearInterval(countdownInterval); };
  }, [loading]);

  const handleStart = async () => {
    if (!businessType || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setLoadingStep(0);
    setCountdown(10);
    setError('');
    try {
      await demoLogin(businessType, businessName || '', priceLevel, city);
      setLoadingDone(true);
      setTimeout(() => navigate('/dashboard'), 700);
    } catch {
      setLoading(false);
      loadingRef.current = false;
      setError(l('حدث خطأ، حاول مرة أخرى', 'Something went wrong, try again'));
    }
  };

  // ===== LOADING SCREEN =====
  if (loading) {
    const steps = LOADING_STEPS[isAr ? 'ar' : 'en'];
    const progress = loadingDone ? 100 : ((loadingStep + 1) / steps.length) * 95;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 relative overflow-hidden" dir={dir}
        style={{ background: '#020617' }}>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-[0.07] animate-pulse"
          style={{ background: `radial-gradient(circle, ${accentColor}, transparent)`, filter: 'blur(80px)' }} />
        <div className="relative z-10 text-center max-w-lg w-full">
          <div className="relative mx-auto mb-10" style={{ width: 110, height: 110 }}>
            <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(from 0deg, ${accentColor}00, ${accentColor}60, ${accentColor}00)`, animation: 'onb-spin 3s linear infinite' }} />
            <div className="absolute inset-3 rounded-full flex items-center justify-center" style={{ background: '#0F172A', boxShadow: `0 0 60px ${accentColor}30` }}>
              <span style={{ fontSize: 42 }}>{loadingDone ? '✨' : selectedType?.emoji || '🍽️'}</span>
            </div>
          </div>
          <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {loadingDone ? l('جاهز!', 'Ready!') : <>{l('جاري إنشاء', 'Creating')} <span style={{ color: accentColor }}>{businessName || l(selectedType?.name_ar, selectedType?.name_en)}</span></>}
          </h2>
          {!loadingDone && countdown > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-3 mt-2" style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}20` }}>
              <Clock className="w-3.5 h-3.5" style={{ color: accentColor }} />
              <span className="text-sm font-bold tabular-nums" style={{ color: accentColor }}>{countdown}</span>
              <span className="text-xs text-white/40">{l('ثوان', 'seconds')}</span>
            </div>
          )}
          <p className="text-sm text-white/30 mb-8">{loadingDone ? l('جاري نقلك...', 'Redirecting...') : l('جاري تجهيز بيئة كاملة مخصصة لك', 'Preparing your personalized environment')}</p>
          <div className="space-y-2.5 text-start mb-8">
            {steps.map((s, i) => {
              const StepIcon = s.icon;
              const isDone = loadingDone || i < loadingStep;
              const isCurrent = !loadingDone && i === loadingStep;
              return (
                <div key={i} className="flex items-center gap-3 transition-all duration-700"
                  style={{ opacity: i <= loadingStep || loadingDone ? 1 : 0.12, transform: i <= loadingStep || loadingDone ? 'translateX(0)' : `translateX(${isAr ? '16px' : '-16px'})` }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: isDone ? 'rgba(16,185,129,0.1)' : isCurrent ? `${accentColor}12` : 'transparent', border: `1.5px solid ${isDone ? '#10B98140' : isCurrent ? `${accentColor}35` : 'rgba(255,255,255,0.04)'}` }}>
                    {isDone ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : isCurrent ? <StepIcon className="w-3.5 h-3.5 animate-pulse" style={{ color: accentColor }} /> : <StepIcon className="w-3 h-3 text-white/10" />}
                  </div>
                  <span className="text-sm" style={{ color: isDone ? 'rgba(16,185,129,0.7)' : isCurrent ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.1)' }}>{s.text}</span>
                </div>
              );
            })}
          </div>
          <div className="h-1 rounded-full bg-white/5"><div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progress}%`, background: loadingDone ? '#10B981' : `linear-gradient(90deg, ${accentColor}, #10B981)` }} /></div>
        </div>
        <style>{`@keyframes onb-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ===== MAIN: SPLIT SCREEN =====
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" dir={dir}
      style={{ background: '#020617', fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}>

      {/* Lang toggle */}
      <button onClick={toggleLanguage} data-testid="onboard-lang-toggle"
        className="fixed top-4 start-4 z-30 px-3 py-1.5 rounded-lg text-xs font-medium text-white/40 hover:text-white/70 transition-colors"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {lang === 'ar' ? 'EN' : 'عربي'}
      </button>

      {/* ===== LEFT PANEL: Controls ===== */}
      <div className="flex-1 flex flex-col min-h-screen lg:max-h-screen overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <Link to="/" className="flex items-center gap-2 group" data-testid="onboard-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>Zouq AI</span>
          </Link>
          <span className="text-[10px] text-white/15">{l('46 نوع · 1139 صنف', '46 types · 1139 items')}</span>
        </div>

        {/* Title */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-extrabold text-white mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>
            {l('اختر نوع مشروعك', 'Choose your business type')}
          </h1>
          <p className="text-xs text-white/25">{l('شاهد معاينة حية فوراً', 'See a live preview instantly')}</p>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
          <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); }}
            placeholder={l('ابحث...', 'Search...')} data-testid="onboard-search"
            className="w-full h-9 ps-8 pe-3 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white placeholder:text-white/15 focus:outline-none focus:border-white/15" />
        </div>

        {/* Category tabs */}
        {!searchQuery && (
          <div className="flex gap-1 mb-3 overflow-x-auto pb-0.5" data-testid="onboard-category-tabs">
            {sortedGroups.map(g => {
              const info = CATEGORY_GROUPS[g] || { ar: g, en: g, icon: '📁' };
              const count = (groupedTypes[g] || []).length;
              return (
                <button key={g} onClick={() => setActiveGroup(g)} data-testid={`onboard-group-${g}`}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap"
                  style={{
                    background: activeGroup === g ? `${accentColor}10` : 'transparent',
                    border: `1px solid ${activeGroup === g ? `${accentColor}25` : 'rgba(255,255,255,0.04)'}`,
                    color: activeGroup === g ? accentColor : 'rgba(255,255,255,0.35)',
                  }}>
                  <span className="text-xs">{info.icon}</span> {isAr ? info.ar : info.en} <span className="opacity-40">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Types grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-4 flex-shrink-0" data-testid="onboard-type-list">
          {filteredTypes.map(type => {
            const isActive = businessType === type.id;
            return (
              <button key={type.id} data-testid={`onboard-type-${type.id}`}
                onClick={() => handleSelectType(type)}
                className="flex items-center gap-2 p-2.5 rounded-lg text-start transition-all duration-200"
                style={{
                  background: isActive ? `${type.color || accentColor}10` : 'rgba(255,255,255,0.015)',
                  border: `1px solid ${isActive ? `${type.color || accentColor}40` : 'rgba(255,255,255,0.04)'}`,
                }}>
                <span style={{ fontSize: 20 }}>{type.emoji || '🍽️'}</span>
                <span className="text-[11px] font-medium truncate" style={{ color: isActive ? (type.color || accentColor) : 'rgba(255,255,255,0.45)' }}>
                  {isAr ? type.name_ar : type.name_en}
                </span>
              </button>
            );
          })}
          {filteredTypes.length === 0 && <div className="col-span-full text-center py-6 text-white/15 text-xs">{l('لا توجد نتائج', 'No results')}</div>}
        </div>

        {/* Details section - appears after type selection */}
        {selectedType && (
          <div className="mt-auto pt-4 space-y-3 border-t border-white/[0.04]" style={{ animation: 'onb-fadeIn 0.3s ease-out' }}>
            {/* Business name */}
            <div>
              <label className="text-[11px] text-white/30 mb-1 flex items-center gap-1">
                <Building2 className="w-3 h-3" />{l('اسم المشروع', 'Name')} <span className="text-white/15">({l('اختياري', 'optional')})</span>
              </label>
              <input value={businessName} onChange={e => setBusinessName(e.target.value)}
                placeholder={l(selectedType.name_ar, selectedType.name_en)}
                data-testid="onboard-business-name"
                className="w-full h-9 px-3 rounded-lg text-sm bg-white/[0.04] border border-white/[0.06] text-white placeholder:text-white/15 focus:outline-none focus:border-white/15" />
            </div>

            {/* City + Price in one row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/30 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{l('المدينة', 'City')}</label>
                <div className="flex flex-wrap gap-1">
                  {CITIES.map(c => (
                    <button key={c.id} data-testid={`onboard-city-${c.id}`} onClick={() => setCity(c.id)}
                      className="px-2 py-1 rounded text-[10px] font-medium transition-all"
                      style={{
                        background: city === c.id ? `${accentColor}12` : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${city === c.id ? `${accentColor}30` : 'rgba(255,255,255,0.04)'}`,
                        color: city === c.id ? accentColor : 'rgba(255,255,255,0.35)',
                      }}>
                      {isAr ? c.ar : c.en}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-white/30 mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" />{l('الأسعار', 'Pricing')}</label>
                <div className="flex gap-1">
                  {PRICE_LEVELS.map(p => (
                    <button key={p.id} data-testid={`onboard-price-${p.id}`} onClick={() => setPriceLevel(p.id)}
                      className="flex-1 py-1.5 rounded text-[10px] font-bold text-center transition-all"
                      style={{
                        background: priceLevel === p.id ? `${p.color}12` : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${priceLevel === p.id ? `${p.color}35` : 'rgba(255,255,255,0.04)'}`,
                        color: priceLevel === p.id ? p.color : 'rgba(255,255,255,0.3)',
                      }}>
                      {isAr ? p.ar : p.en}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA */}
            <button onClick={handleStart} data-testid="onboard-next-btn"
              className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99]"
              style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`, boxShadow: `0 8px 32px ${accentColor}25` }}>
              <Sparkles className="w-4 h-4" /> {l('ابدأ التجربة المجانية', 'Start Free Trial')}
            </button>
            {error && <p className="text-red-400 text-[11px] text-center" data-testid="onboard-error">{error}</p>}
            <p className="text-center text-[10px] text-white/15">{l('بدون تسجيل · بدون بطاقة ائتمان · 14 يوم', 'No signup · No credit card · 14 days')}</p>
          </div>
        )}

        {/* Login link */}
        <div className="mt-3 text-center">
          <Link to="/login" data-testid="onboard-login-link" className="text-[11px] text-white/20 hover:text-amber-400/60 transition-colors">
            {l('لدي حساب بالفعل', 'Already have an account?')}
          </Link>
        </div>
      </div>

      {/* ===== RIGHT PANEL: Live Preview ===== */}
      <div className="hidden lg:flex lg:w-[420px] xl:w-[460px] flex-col items-center justify-center p-6 relative"
        style={{ background: '#0A0F1E', borderInlineStart: '1px solid rgba(255,255,255,0.04)' }}>
        {/* Glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full opacity-[0.06]"
          style={{ background: `radial-gradient(circle, ${accentColor}, transparent)`, filter: 'blur(80px)', transition: 'all 0.5s' }} />

        {!selectedType ? (
          /* Empty state */
          <div className="relative z-10 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white/[0.03] flex items-center justify-center border border-white/[0.05]">
              <span style={{ fontSize: 36 }}>🍽️</span>
            </div>
            <p className="text-sm text-white/25 mb-1">{l('معاينة حية', 'Live Preview')}</p>
            <p className="text-xs text-white/15">{l('اختر نوع مشروعك لمشاهدة المعاينة', 'Select a business type to see preview')}</p>
          </div>
        ) : (
          /* Live preview card */
          <div className="relative z-10 w-full max-w-[320px]" style={{ animation: 'onb-fadeIn 0.4s ease-out' }}>
            {/* Phone frame */}
            <div className="rounded-[24px] overflow-hidden" style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.06)', boxShadow: `0 0 80px ${accentColor}08` }}>
              {/* Header */}
              <div className="p-4 pb-3" style={{ background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}20` }}>
                    <span style={{ fontSize: 26 }}>{selectedType.emoji}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate" style={{ fontFamily: 'Cairo, sans-serif' }}>
                      {businessName || l(selectedType.name_ar, selectedType.name_en)}
                    </p>
                    <p className="text-[11px] truncate" style={{ color: `${accentColor}90` }}>
                      {CITIES.find(c => c.id === city)?.[isAr ? 'ar' : 'en']} · {PRICE_LEVELS.find(p => p.id === priceLevel)?.[isAr ? 'ar' : 'en']}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="px-4 py-3">
                <p className="text-[10px] text-white/25 mb-2 font-medium">{l('عينة من المنيو', 'Menu Sample')}</p>
                {previewLoading ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-white/20 animate-spin" /></div>
                ) : preview?.sample_items?.length > 0 ? (
                  <div className="space-y-1.5">
                    {preview.sample_items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg transition-all"
                        style={{ background: 'rgba(255,255,255,0.02)', animation: `onb-fadeIn ${0.3 + i * 0.1}s ease-out` }}>
                        <span className="text-xs text-white/60 truncate flex-1">{isAr ? item.name_ar : item.name}</span>
                        <span className="text-xs font-bold ms-2 whitespace-nowrap" style={{ color: accentColor }}>{item.price} {l('ر.س', 'SAR')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-white/15 text-xs">{l('لا توجد بيانات', 'No data')}</div>
                )}
              </div>

              {/* Stats */}
              <div className="px-4 py-3 border-t border-white/[0.04]">
                <p className="text-[10px] text-white/25 mb-2 font-medium">{l('ستحصل على', 'You will get')}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { icon: Wand2, val: `${preview?.total_items || '...'} ${l('صنف', 'items')}`, label: l('المنيو', 'Menu') },
                    { icon: ShoppingBag, val: `15 ${l('طلب', 'orders')}`, label: l('طلبات', 'Orders') },
                    { icon: BarChart3, val: l('كامل', 'Full'), label: l('تحليلات', 'Analytics') },
                    { icon: Bot, val: l('جاهز', 'Ready'), label: l('وكيل ذكي', 'AI Agent') },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <s.icon className="w-3 h-3 flex-shrink-0" style={{ color: `${accentColor}60` }} />
                      <div>
                        <p className="text-[10px] font-bold text-white/50">{s.val}</p>
                        <p className="text-[9px] text-white/20">{s.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              {preview?.insights && (
                <div className="px-4 py-3 border-t border-white/[0.04]">
                  <div className="flex gap-3">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3 h-3 text-emerald-400/50" />
                      <span className="text-[10px] text-white/30">{l('متوسط الطلب', 'Avg order')}: <b className="text-white/50">{preview.insights.avg_order_value} {l('ر.س', 'SAR')}</b></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3 h-3 text-blue-400/50" />
                      <span className="text-[10px] text-white/30">{l('توصيل', 'Delivery')}: <b className="text-white/50">{preview.insights.delivery_pct}%</b></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Badge below */}
            <div className="text-center mt-3">
              <span className="text-[10px] text-white/15">{l('المعاينة تتغير فوراً مع كل اختيار', 'Preview updates instantly with each selection')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Mobile preview (shows below types when selected) */}
      {selectedType && preview && (
        <div className="lg:hidden px-4 pb-4 -mt-2">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span style={{ fontSize: 18 }}>{selectedType.emoji}</span>
              <span className="text-xs font-bold text-white/60">{businessName || l(selectedType.name_ar, selectedType.name_en)}</span>
              <span className="text-[10px] text-white/25 ms-auto">{preview.total_items} {l('صنف', 'items')}</span>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {preview.sample_items?.slice(0, 4).map((item, i) => (
                <div key={i} className="flex-shrink-0 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <span className="text-[10px] text-white/50">{isAr ? item.name_ar : item.name}</span>
                  <span className="text-[10px] font-bold ms-1.5" style={{ color: accentColor }}>{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes onb-fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes onb-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
